import { Trans, useLingui } from '@lingui/react/macro';

import Accordion from '@/design_system/Accordion';
import Button from '@/design_system/Button';
import { Separator } from '@/design_system/Menu/Menu';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconEdit from '@/icons/Edit.svg';
import { useArticleName } from '@/models/article';
import { hasPermission } from '@/models/user';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useCurrentSession } from '@/services/auth';
import useViewPort from '@/utils/useViewport';

import { ArticleCustomsDrawer } from '../../ArticleCustomsDrawer';
import { ArticleInformationFormDrawer } from '../../ArticleInformationFormDrawer';

import { ActionsSummary } from './ActionsSummary';
import { ArticleAuthenticity } from './ArticleAuthenticity';
import { ArticleCustomsDetails } from './ArticleCustomsDetails';
import { ArticleDetails } from './ArticleDetails';
import { ArticlePriceAndEstimatedDueDate } from './ArticlePriceAndEstimatedDueDate';
import { ArticleWarranty } from './ArticleWarranty';
import { ArticleWorkshopPayment } from './ArticleWorkshopPayment';
import { RequestsHistory } from './RequestsHistory';

export const ArticleInfoSidePanel = () => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();
  const { view: requestView } = useRequestContext();
  const { article, state, view, errors } = useArticleContext();
  const { isMobile } = useViewPort();
  const articleName = useArticleName({ article });

  const canEditArticleInfo =
    hasPermission(currentSession, 'edit_article', [
      {
        organizationId: article.organizationId,
      },
      article.workshopId
        ? {
            entityId: article.workshopId,
          }
        : null,
    ]) && !article.archivedAt;

  const canEditArticleCustomsInfo =
    hasPermission(currentSession, 'edit_customs_data', [
      {
        organizationId: article.organizationId,
      },
      article.workshopId
        ? {
            entityId: article.workshopId,
          }
        : null,
    ]) && !article.archivedAt;

  const showWorkshopPaymentSection =
    article.repairedAt !== null &&
    !!article.workshop?.workshopConfig?.nonDigitalized &&
    (article.workshopPaymentStrategy === 'store-cash-out' ||
      article.workshopPaymentStrategy === 'handled-by-accounting-team');

  const showCustomsInformations = article.areCustomsExpected || article.wentThroughCustoms;
  const canEditCustoms = canEditArticleCustomsInfo && requestView.customs?.enabled;

  return (
    <Stack gap="0.5rem">
      {isMobile ? (
        <Stack padding="1rem 0.75rem 0.75rem" gap="0.5rem">
          <Title as="h1">{articleName}</Title>
        </Stack>
      ) : (
        <Stack padding="0.5rem 0" gap="0.75rem">
          <ArticlePriceAndEstimatedDueDate />
        </Stack>
      )}
      <Separator />

      {showWorkshopPaymentSection && (
        <>
          <Accordion
            openByDefault
            trigger={
              <Paragraph weight="medium">
                <Trans id="article.workshop-payment.title">Workshop payment</Trans>
              </Paragraph>
            }
          >
            <ArticleWorkshopPayment />
          </Accordion>
          <Separator />
        </>
      )}

      <Stack ariaLabel={t({ id: 'article.information.title', message: 'Information' })}>
        <Accordion
          openByDefault
          trigger={
            <Paragraph weight="medium">
              <Trans id="article.information.title">Information</Trans>
            </Paragraph>
          }
          isInvalid={errors.details?.hasError}
        >
          <ArticleDetails />
          {canEditArticleInfo && (
            <Button
              variant="secondary"
              onPress={() => state.infoPanel.setIsOpen(true)}
              size="small"
              ariaLabel={t({ id: 'article.edit.actions.edit-article', message: 'Edit' })}
            >
              <IconEdit />
              <Trans id="article.edit.actions.edit-article">Edit</Trans>
            </Button>
          )}
        </Accordion>

        <ArticleInformationFormDrawer />
      </Stack>

      {showCustomsInformations && (
        <>
          <Separator />
          <Accordion
            openByDefault
            trigger={
              <Paragraph weight="medium">
                <Trans id="article.customs.title">Customs information</Trans>
              </Paragraph>
            }
            isInvalid={!!errors?.customs}
          >
            <ArticleCustomsDetails />
            {canEditCustoms && (
              <Button
                variant="secondary"
                onPress={() => state.customsPanel.setIsOpen(true)}
                size="small"
                ariaLabel={t({ id: 'article.edit.actions.edit-article', message: 'Edit' })}
              >
                <IconEdit />
                <Trans id="article.edit.actions.edit-article">Edit</Trans>
              </Button>
            )}
          </Accordion>

          <ArticleCustomsDrawer />
        </>
      )}

      {view.warranty.shown && view.warranty.type === 'warranty' && !view.warranty.editable && (
        <>
          <Separator />

          <Accordion
            openByDefault
            trigger={
              <Paragraph weight="medium">
                <Trans id="article.warranty.title">Warranty</Trans>
              </Paragraph>
            }
            isInvalid={errors.warranty?.hasError}
          >
            <ArticleWarranty />
          </Accordion>
        </>
      )}

      {view.warranty.shown && view.warranty.type === 'authenticity' && !view.warranty.editable && (
        <>
          <Separator />

          <Accordion
            openByDefault
            trigger={
              <Paragraph weight="medium">
                <Trans id="article.authenticity.title">Authenticity</Trans>
              </Paragraph>
            }
            isInvalid={errors.warranty?.hasError}
          >
            <ArticleAuthenticity />
          </Accordion>
        </>
      )}

      {view.details.serialNumber.history.shown && (
        <>
          <Separator />
          <Accordion
            openByDefault
            trigger={
              <Stack row gap="0.5rem" alignItems="center">
                <Paragraph weight="medium">
                  <Trans id="article.actions-summary.title">Actions summary</Trans>
                </Paragraph>
              </Stack>
            }
          >
            <ActionsSummary />
          </Accordion>

          <Separator />

          <Accordion
            openByDefault
            trigger={
              <Stack row gap="0.5rem" alignItems="center">
                <Paragraph weight="medium">
                  <Trans id="article.requests-history.title">Requests history</Trans>
                </Paragraph>
              </Stack>
            }
          >
            <RequestsHistory />
          </Accordion>
        </>
      )}
    </Stack>
  );
};
